import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { PLACEHOLDER_MESSAGES, VALIDATION_MESSAGES } from "./constants";
import "./styles/Step2Form.scss";
import { useStateMachine } from "little-state-machine";
import { updateAction } from "scenes/ProjectMultiStepForm/store";
import QuerySelect from "./components/QuerySelect";
import { graphql } from "react-apollo";
import { updateProjectInformation } from "../../../Step1/components/Step1Form/mutation";
import TooltipField from "scenes/ProjectMultiStepForm/components/TooltipField";
import * as Yup from "yup";
import CustomSelect from "utils/components/CustomSelect";
import { UNIT_MEASURE_VALUES } from "./constants/select-values";
import proquinalHistory from "services/browser-history";

const Step2Form = ({ updateProjectInformation }) => {
  const intl = useIntl();
  const { state, actions } = useStateMachine({ updateAction });

  const initialValues = {
    salesRepresentative: state?.salesRepresentative || [],
    invoiceRecipient: state?.invoiceRecipient || [],
    projectSpecifier: state?.projectSpecifier || [],
    projectManufacturer: state?.projectManufacturer || [],
    finalClient: state?.finalClient || [],
    soldMeters: state?.soldMeters || "",
    unitOfMeasure: state?.unitOfMeasure || ""
  }

  const validationSchema = Yup.object({
    soldMeters: Yup.number().positive(
      intl.locale === "es" ? "Debe ser positivo" : "Must be positive"
    ),
    unitOfMeasure: Yup.string().when("soldMeters", {
      is: (soldMeters) => soldMeters && soldMeters > 0,
      then: () => Yup.string().required(
        intl.formatMessage(VALIDATION_MESSAGES.required)
      ),
      otherwise: () => Yup.string().notRequired(),
    }),
  })
  
  const handleSubmit = (values) => {
    const {
      salesRepresentative,
      invoiceRecipient,
      projectSpecifier,
      projectManufacturer,
      finalClient,
      soldMeters,
      unitOfMeasure
    } = values;

    const input = { 
      projectId: state?.projectId,
      finalClient: finalClient[0]?.value,
      manufacturer: projectManufacturer[0]?.value,
      specifiedBy: projectSpecifier[0]?.value,
      salesMember: salesRepresentative[0]?.value,
      invoicedTo: invoiceRecipient[0]?.value,
      salesMember: salesRepresentative[0]?.value,
      invoicedTo: invoiceRecipient[0]?.value,
      selledMts: soldMeters,
      unitOfMeasure: soldMeters && UNIT_MEASURE_VALUES.find((item) => item.value === unitOfMeasure)?.value
    }
    
    const filteredInput = Object.fromEntries(
      Object.entries(input).filter(([key, value]) => value)
    );

    return updateProjectInformation({
      variables: {
        ...filteredInput
      }
    })
      .then((response) => {
        const { updateProjectData } = response.data;

        if (updateProjectData?.project) {
          actions.updateAction({
            ...state,
            ...values,
            projectId: updateProjectData?.project.id,
          });

          proquinalHistory.push({
            pathname: "/project/creation-form/step3"
          })
        }
      })
      .catch((err) => {
        const { graphQLErrors, netWorkError } = err;

        if (graphQLErrors || netWorkError) {
          console.error("Error update Project GraphQL:", graphQLErrors);
          console.error("Error update Project Network:", netWorkError);
        }
      });
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({
        setFieldValue,
        values: {
          salesRepresentative,
          invoiceRecipient,
          projectSpecifier,
          projectManufacturer,
          finalClient,
          soldMeters,
          unitOfMeasure,
        },
        isSubmitting,
      }) => (
        <Form
          id="step-0-form"
          onKeyDown={(e) => e && e.key === "Enter" && e.preventDefault()}
        >
          <div className="step-0-form__container d-flex flex-column">
            <div className="step-0-form__selects d-lg-flex align-items-end">
              <div className="col-12 col-lg-6 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label d-inline p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="salesRepresentative"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step2.field.salesRepresentative"
                    defaultMessage="Comercial que vendio el proyecto"
                  />
                </label>
                <TooltipField
                  tooltipMessage={
                    <FormattedMessage
                      id="ProjectStepForm.tooltip.salesRepresentative"
                      defaultMessage="Si no existe el comercial, contacta a karen.pulido@spradling.group para registrarlo"
                    />
                  }
                />
                <QuerySelect
                  name="salesRepresentative"
                  placeholder={intl.formatMessage(
                    PLACEHOLDER_MESSAGES.salesRepresentative
                  )}
                  initialValue={salesRepresentative}
                  updateField={setFieldValue}
                  isCommercial
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="salesRepresentative"
                />
              </div>
              <div className="col-12 col-6 px-0">
                <label
                  className="step-0-form__label d-inline p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="area"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step2.field.invoiceRecipient"
                    defaultMessage="A quién se le facturó el proyecto"
                  />
                </label>
                <TooltipField
                  tooltipMessage={
                    <FormattedMessage
                      id="ProjectStepForm.tooltip.provider"
                      defaultMessage="Si no aparece la opción, escribe el valor completo y haz clic en “Crear proveedor”"
                    />
                  }
                />
                <QuerySelect
                  name="invoiceRecipient"
                  placeholder={intl.formatMessage(
                    PLACEHOLDER_MESSAGES.invoiceRecipient
                  )}
                  initialValue={invoiceRecipient}
                  updateField={setFieldValue}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="invoiceRecipient"
                />
              </div>
            </div>
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-6 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="businessUnit"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step2.field.projectSpecifier"
                    defaultMessage="Quién especificó el proyecto"
                  />
                </label>
                <TooltipField
                  tooltipMessage={
                    <FormattedMessage
                      id="ProjectStepForm.tooltip.provider"
                      defaultMessage="Si no aparece la opción, escribe el valor completo y haz clic en “Crear proveedor”"
                    />
                  }
                />
                <QuerySelect
                  name="projectSpecifier"
                  placeholder={intl.formatMessage(
                    PLACEHOLDER_MESSAGES.projectSpecifier
                  )}
                  initialValue={projectSpecifier}
                  updateField={setFieldValue}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="projectSpecifier"
                />
              </div>
              <div className="col-12 col-6 px-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="area"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step2.field.projectManufacturer"
                    defaultMessage="Quién manufacturó el proyecto"
                  />
                </label>
                <TooltipField
                  tooltipMessage={
                    <FormattedMessage
                      id="ProjectStepForm.tooltip.provider"
                      defaultMessage="Si no aparece la opción, escribe el valor completo y haz clic en “Crear proveedor”"
                    />
                  }
                />
                <QuerySelect
                  name="projectManufacturer"
                  placeholder={intl.formatMessage(
                    PLACEHOLDER_MESSAGES.projectManufacturer
                  )}
                  initialValue={projectManufacturer}
                  updateField={setFieldValue}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="projectManufacturer"
                />
              </div>
            </div>
            <div className="step-0-form__selects d-lg-flex align-items-end">
              <div className="col-12 col-lg-12 px-0 mb-lg-3 mb-lg-0">
                <label
                  className="step-0-form__label d-inline p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="businessUnit"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step2.field.finalClient"
                    defaultMessage="¿Cuál fue la ubicación final de la ejecución del proyecto? {br} (por ejemplo, el estadio de Oregón, Universidad de los Andes)"
                    values={{ br: <br /> }}
                  />
                </label>
                <TooltipField
                  tooltipMessage={
                    <FormattedMessage
                      id="ProjectStepForm.tooltip.provider"
                      defaultMessage="Si no aparece la opción, escribe el valor completo y haz clic en “Crear proveedor”"
                    />
                  }
                />
                <QuerySelect
                  name="finalClient"
                  placeholder={intl.formatMessage(
                    PLACEHOLDER_MESSAGES.finalClient
                  )}
                  initialValue={finalClient}
                  updateField={setFieldValue}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="finalClient"
                />
              </div>
            </div>
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-6 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label d-block p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="area"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step2.field.soldMeters"
                    defaultMessage="Número de metros/yardas vendidas"
                  />
                </label>
                <Field
                  id="soldMeters"
                  name="soldMeters"
                  value={soldMeters}
                  className="form-control bg-transparent form-control-placeholder"
                  type="number"
                  placeholder={intl.formatMessage(
                    PLACEHOLDER_MESSAGES.soldMeters
                  )}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="soldMeters"
                />
              </div>
              <div className="col-12 col-6 px-0">
                <label
                  className="step-0-form__label d-block p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="unitOfMeasure"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step2.field.unitOfMeasure"
                    defaultMessage="Unidad de medida"
                  />
                </label>
                <CustomSelect
                  config={{
                    name: "unitOfMeasure",
                    initialValue: 
                      soldMeters && (unitOfMeasure && UNIT_MEASURE_VALUES.find(({ value }) => value === unitOfMeasure)?.label) || "",
                    disabled: !soldMeters,
                    disabledPlaceholder: (
                      <FormattedMessage 
                        id="ProjectStepForm.step2.field.unitOfMeasure.disabled"
                        defaultMessage="Diligencie primero el número de metros vendidos"
                      />
                    ),
                    placeholderMessage: intl.formatMessage(
                      PLACEHOLDER_MESSAGES.unitOfMeasure
                    ),
                  }}
                  selectValues={UNIT_MEASURE_VALUES}
                  updateField={setFieldValue}
                  returnValue="id"
                />
              </div>
            </div>
            <button
              type="submit"
              className="step-0-form__submit-btn px-5 py-2 mt-4"
              disabled={isSubmitting}
            >
              <FormattedMessage
                id="ProjectStepForm.button.continue"
                defaultMessage="Continuar"
              />
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default graphql(updateProjectInformation, { name: "updateProjectInformation" })(Step2Form);
